import { Check, ChevronDown, Copy, Download, FileCode2, FileSpreadsheet, FileText, FileType2, Flag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { toDocx } from '../../minutesDocx'
import { downloadText, fileSlug, toCsv, toMarkdown, toPlainText, type MinutesHeader } from '../../minutesExport'
import { PRIORITIES, type Meeting, type Priority } from '../../types'
import { useCrm } from '../../ui'

const TONE: Record<Priority, string> = {
  high: 'bg-destructive/10 text-destructive',
  medium: 'bg-warning/15 text-warning-strong',
  low: 'bg-muted text-muted-foreground',
}

type PickerProps = { value: Priority; onChange?: (p: Priority) => void; large?: boolean; compact?: boolean }

/** Öncelik rozeti; `onChange` verilirse tıklanınca öncelik seçilir. */
export function PriorityPicker({ value, onChange, large, compact }: PickerProps) {
  const { t } = useTranslation()
  const label = t(`crm.priority.${value}`)
  const chip = (
    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-md font-medium', TONE[value], large ? 'h-12 px-3 text-sm' : compact ? 'h-6 px-1.5 text-[11px]' : 'h-9 px-2.5 text-xs')}>
      <Flag className={large ? 'size-4' : 'size-3'} aria-hidden />
      {label}
      {onChange ? <ChevronDown className="size-3 opacity-60" aria-hidden /> : null}
    </span>
  )
  if (!onChange) return chip
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={t('crm.minutes.priorityLabel', { priority: label })}>
        {chip}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-36">
        {PRIORITIES.slice()
          .reverse()
          .map((p) => (
            <DropdownMenuItem key={p} onSelect={() => onChange(p)}>
              <Flag className={cn(p === 'high' ? 'text-destructive' : p === 'medium' ? 'text-warning' : 'text-muted-foreground')} />
              {t(`crm.priority.${p}`)}
              {p === value ? <Check className="ml-auto" /> : null}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Notları panoya kopyalar ya da TXT / Markdown / CSV olarak indirir. Dosya tarayıcıda üretilir. */
export function MinutesExportMenu({ meeting: m }: { meeting: Meeting }) {
  const { t } = useTranslation()
  const { customerName, staffName } = useCrm()
  const header: MinutesHeader = {
    title: t('crm.minutes.title'),
    customer: customerName(m.customerId),
    date: fmtDate(m.date),
    time: m.end ? `${m.start}–${m.end}` : m.start,
    staff: staffName(m.staffId),
    priorityLabel: (p) => t(`crm.priority.${p}`),
    doneLabel: t('crm.minutes.statusDone'),
    openLabel: t('crm.minutes.statusOpen'),
    summary: (done, total) => t('crm.minutes.docSummary', { done, total }),
    columns: { no: '#', note: t('crm.minutes.colNote'), priority: t('crm.minutes.colPriority'), status: t('crm.minutes.colStatus') },
  }
  const base = `toplanti-notlari-${fileSlug(header.customer)}-${m.date}`
  const disabled = m.minutes.length === 0

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(toPlainText(m.minutes, header))
      toast.success(t('crm.minutes.copied', { count: m.minutes.length }))
    } catch {
      toast.error(t('crm.links.copyFailed'))
    }
  }

  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={disabled} onClick={() => void copy()}>
        <Copy />
        <span className="hidden sm:inline">{t('crm.minutes.copy')}</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 px-2" disabled={disabled}>
            <Download />
            <span className="hidden sm:inline">{t('crm.minutes.export')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuLabel>{t('crm.minutes.exportAs')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => downloadText(`${base}.docx`, toDocx(m.minutes, header), 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')}>
            <FileType2 className="text-info" />
            {t('crm.minutes.exportDocx')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => downloadText(`${base}.txt`, toPlainText(m.minutes, header), 'text/plain')}>
            <FileText />
            {t('crm.minutes.exportTxt')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => downloadText(`${base}.md`, toMarkdown(m.minutes, header), 'text/markdown')}>
            <FileCode2 />
            {t('crm.minutes.exportMd')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => downloadText(`${base}.csv`, toCsv(m.minutes, header), 'text/csv')}>
            <FileSpreadsheet />
            {t('crm.minutes.exportCsv')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
